// Guest cart management using localStorage

export interface GuestCartItem {
  product_id: string;
  variant_id?: string;
  qty: number;
  price_snapshot: number;
  selected_option_value_ids?: string[];
  selected_options?: {
    collaboration_bundle_id?: string;
    collaboration_bundle_slug?: string;
    collaboration_bundle_title?: string;
    bundle_total_price?: number;
    bundle_retail_value?: number;
    bundle_savings?: number;
    option_value_ids?: string[];
  };
}

const GUEST_CART_KEY = 'guest_cart';

export const getGuestCart = (): GuestCartItem[] => {
  try {
    const cart = localStorage.getItem(GUEST_CART_KEY);
    return cart ? JSON.parse(cart) : [];
  } catch {
    return [];
  }
};

export const addToGuestCart = (item: GuestCartItem): void => {
  const cart = getGuestCart();
  
  // For bundle items, don't merge - each bundle addition is separate
  const isBundleItem = !!item.selected_options?.collaboration_bundle_id;
  
  const existingIndex = isBundleItem 
    ? -1 // Never merge bundle items
    : cart.findIndex(
        (i) => i.product_id === item.product_id && 
               i.variant_id === item.variant_id &&
               !i.selected_options?.collaboration_bundle_id // Don't merge with existing bundle items
      );

  if (existingIndex >= 0) {
    cart[existingIndex].qty += item.qty;
  } else {
    cart.push(item);
  }

  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
  // Dispatch event for badge updates
  window.dispatchEvent(new CustomEvent('cart:changed'));
};

export const updateGuestCartItem = (productId: string, variantId: string | undefined, qty: number): void => {
  const cart = getGuestCart();
  const index = cart.findIndex(
    (i) => i.product_id === productId && i.variant_id === variantId
  );
  
  if (index >= 0) {
    cart[index].qty = qty;
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cart));
    window.dispatchEvent(new CustomEvent('cart:changed'));
  }
};

export const removeFromGuestCart = (productId: string, variantId?: string): void => {
  const cart = getGuestCart();
  const filtered = cart.filter(
    (i) => !(i.product_id === productId && i.variant_id === variantId)
  );
  localStorage.setItem(GUEST_CART_KEY, JSON.stringify(filtered));
  window.dispatchEvent(new CustomEvent('cart:changed'));
};

export const clearGuestCart = (): void => {
  localStorage.removeItem(GUEST_CART_KEY);
  window.dispatchEvent(new CustomEvent('cart:changed'));
};

export const mergeGuestCartToDatabase = async (
  supabase: any,
  userId: string
): Promise<void> => {
  const guestItems = getGuestCart();
  if (guestItems.length === 0) return;

  // Get or create user's cart
  let { data: cart } = await supabase
    .from('carts')
    .select('id')
    .eq('profile_id', userId)
    .maybeSingle();

  if (!cart) {
    const { data: newCart } = await supabase
      .from('carts')
      .insert({ profile_id: userId })
      .select('id')
      .single();
    cart = newCart;
  }

  if (!cart) return;

  // Insert guest items into database
  for (const item of guestItems) {
    const cartItemData: any = {
      cart_id: cart.id,
      product_id: item.product_id,
      variant_id: item.variant_id,
      qty: item.qty,
      price_snapshot: item.price_snapshot,
    };

    // Handle selected_options (bundle metadata or option value IDs)
    if (item.selected_options) {
      cartItemData.selected_options = item.selected_options;
    } else if (item.selected_option_value_ids && item.selected_option_value_ids.length > 0) {
      cartItemData.selected_options = { option_value_ids: item.selected_option_value_ids };
    }

    await supabase.from('cart_items').insert(cartItemData);
  }

  clearGuestCart();
};
